import type { Meta } from '@upbeat/shared';
import type { FilterState } from '../state/filters';
import { Badge } from './ui/badge';
import { Globe2, Layers, Tag } from 'lucide-react';
import { t } from '../i18n';
import { useLocation } from 'react-router-dom';
import { ROUTE_FILTER_CONFIG, DEFAULT_FILTER_CONFIG } from './Filters';

/** One-line summary of active non-default filters (regions, income, ownership), shown under each page title. */
export function FilterLine({ state, meta }: { state: FilterState; meta: Meta }) {
  const location = useLocation();
  const config = ROUTE_FILTER_CONFIG[location.pathname] ?? DEFAULT_FILTER_CONFIG;

  const part = (
    label: string,
    selected: string[],
    all: number,
    icon: React.ReactNode,
    optionsMap?: Map<string, string>
  ) => {
    if (selected.length === all) return null;
    const text =
      selected.length === 0
        ? t.none
        : selected.map((code) => optionsMap?.get(code) ?? code).join(', ');

    return (
      <Badge
        key={label}
        variant="secondary"
        className="flex items-center gap-1.5 py-1 px-2.5 font-normal tracking-normal text-xs normal-case border-hair bg-glass-2"
      >
        <span className="text-dim">{icon}</span>
        <span className="text-dim font-medium">{label}:</span>
        <span className="font-medium text-txt max-w-[200px] truncate" title={text}>
          {text}
        </span>
      </Badge>
    );
  };

  const regionNames = new Map(meta.regions.map((r) => [r.code, r.label]));
  const badges = [
    config.showRegions ? part(t.flRegions, state.region, meta.regions.length, <Globe2 className="h-3.5 w-3.5" />, regionNames) : null,
    config.showIncome ? part(t.flIncome, state.income, meta.income_groups.length, <Layers className="h-3.5 w-3.5" />) : null,
    config.showOwnership ? part(t.flOwnership, state.ownership, meta.ownerships.length, <Tag className="h-3.5 w-3.5" />) : null,
  ].filter(Boolean);

  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 -mt-3 mb-2">
      {badges}
    </div>
  );
}
